"use client";

import { useEffect, useState } from "react";
import type { OrderOutput } from "@/interfaces";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { numberToMoney } from "@/util/moneyConversion";
import {
  format,
  subMonths,
  isAfter,
  isBefore,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { ptBR } from "date-fns/locale";

interface MonthlyDelinquency {
  month: string;
  amount: number;
  date: Date;
}

interface DelinquencyTrendChartProps {
  orders: OrderOutput[];
}

export function DelinquencyTrendChart({ orders }: DelinquencyTrendChartProps) {
  const [data, setData] = useState<MonthlyDelinquency[]>([]);

  useEffect(() => {
    if (!orders?.length) {
      setData([]);
      return;
    }

    // Get last 6 months
    const today = new Date();
    const months: { month: string; date: Date }[] = [];

    for (let i = 5; i >= 0; i--) {
      const date = subMonths(today, i);
      months.push({
        month: format(date, "MMM/yy", { locale: ptBR }),
        date,
      });
    }

    // Calculate delinquency for each month
    const monthlyData = months.map(({ month, date }) => {
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);

      const monthOrders = orders.filter((order) => {
        if (!order.preparationDate) return false;
        const orderDate = new Date(order.preparationDate);
        return isAfter(orderDate, monthStart) && isBefore(orderDate, monthEnd);
      });

      const totalDelinquency = monthOrders.reduce((total, order) => {
        const paidAmount =
          order.payments?.reduce((sum, payment) => sum + payment.amount, 0) ||
          0;
        return total + (order.totalAmount - paidAmount);
      }, 0);

      return {
        month,
        amount: totalDelinquency,
        date,
      };
    });

    setData(monthlyData);
  }, [orders]);

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground text-center">
          Não há dados de tendência de inadimplência para exibir.
        </p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={data}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis tickFormatter={(value) => numberToMoney(value)} />
        <Tooltip
          formatter={(value: number) => [
            numberToMoney(value),
            "Valor Pendente",
          ]}
          labelFormatter={(label) => `Mês: ${label}`}
        />
        <Line
          type="monotone"
          dataKey="amount"
          name="Valor Pendente"
          stroke="#ef4444"
          activeDot={{ r: 8 }}
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
